import type { TaskType, TeamType, UserType } from '@api';
import {
  MultiSelectField,
  TextareaField,
  TextField,
} from '@hypeit-gmbh/hypeit-ui';
import { SimpleGrid, Stack } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormActions } from '../../../FormActions';
import { PrioritySelect } from '../../../TaskForm/components';
import {
  getTaskFormDefaultValues,
  TaskFormSchemaType,
  useTaskForm,
} from '../../../TaskForm';
import { DetailSection } from '../DetailSection';
import { TaskSystemMetadata } from '../TaskSystemMetadata';

const TRANS_COLUMNS = 'TABLE.COLUMNS';
const TRANS_KEY = 'FORM';

type TaskDetailsEditFormPropsType = {
  task: TaskType;
  onSubmit: (data: TaskFormSchemaType) => Promise<void>;
  onCancel: () => void;
  users: UserType[];
  teams: TeamType[];
};

export const TaskDetailsEditForm: React.FC<TaskDetailsEditFormPropsType> = ({
  task,
  onSubmit,
  onCancel,
  users,
  teams,
}) => {
  const { t } = useTranslation(['tasks', 'common']);
  const form = useTaskForm(getTaskFormDefaultValues(task));

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.firstname} ${user.lastname}`,
  }));

  const teamOptions = teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Stack gap="sm">
              <TextField
                fieldName="title"
                label={t(`${TRANS_KEY}.FIELDS.TITLE`)}
                required
              />
              <TextareaField
                fieldName="description"
                label={t(`${TRANS_KEY}.FIELDS.DESCRIPTION`)}
                required
              />
              <PrioritySelect label={t(`${TRANS_KEY}.FIELDS.PRIORITY`)} />
              <TextField
                fieldName="context"
                label={t(`${TRANS_KEY}.FIELDS.CONTEXT`)}
              />
              <DetailSection label={t(`${TRANS_COLUMNS}.AUDIENCE`)}>
                <Stack gap="sm">
                  <MultiSelectField
                    fieldName="userIds"
                    data={userOptions}
                    label={t(`${TRANS_KEY}.FIELDS.USERS`)}
                    searchable
                  />
                  <MultiSelectField
                    fieldName="teamIds"
                    data={teamOptions}
                    label={t(`${TRANS_KEY}.FIELDS.TEAMS`)}
                    searchable
                  />
                </Stack>
              </DetailSection>
            </Stack>

            <TaskSystemMetadata task={task} />
          </SimpleGrid>

          <FormActions
            onCancel={onCancel}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
